import { createSelector } from 'reselect';
import { dealsAffiliationRequestsSelector } from 'store/state/domainData/selectors/getDealsAffiliationRequests';
import { IDealAffiliationRequest, DealAffiliationStatus } from 'utils/entities';
import { orderBy } from 'lodash';
import { IDealsTableConfig } from 'components/agent-console/deals/deals-section/deals-table/types';
import { getRouteParams, routeSelector } from 'store/state/selectors/router';
import {
  isAgentManagerSelector,
  userProfileSelector,
  agentIdSelector,
  isAgentSelector,
} from 'store/state/domainData/selectors';
import { Route } from 'config/routes';
import { YEAR_BEFORE } from 'components/agent-console/deals/summary-approved/helpers';

const dateFields = new Set([ 'lastUpdated', 'dealDate' ]);

export const routeAgentIdSelector = createSelector(getRouteParams, (p => p.agentId));
export const isMyDealsPageSelector = createSelector(routeSelector, ({ name }) => name === Route.MyDeals);
export const isMyDealsSelector = createSelector([
  isMyDealsPageSelector,
  isAgentManagerSelector,
  isAgentSelector,
], (isMyDealsPage, isAgentManager, isAgent) => {
  return isMyDealsPage || !isMyDealsPage && !isAgentManager && isAgent;
});

export const filteredDealsAffiliationRequestsSelector = createSelector([
  dealsAffiliationRequestsSelector,
  routeAgentIdSelector,
  isAgentManagerSelector,
  userProfileSelector,
  agentIdSelector,
  routeSelector,
  isMyDealsPageSelector,
], (data, agentId, isAgentManager, user, officeManagerAgentId, route, isMyDealsPage) => {
  if (!data || !data.length) return [];

  if (isMyDealsPage && isAgentManager && officeManagerAgentId) {
    return data.filter(d => d.agentId === officeManagerAgentId);
  }

  if (isAgentManager) {
    const currentOfficeDeals = data.filter(item => item.officeId === user.commercialOfficeManagerOfficeId);

    if (agentId) {
      return currentOfficeDeals.filter(item => item.agentId === agentId);
    }
    else {
      return currentOfficeDeals;
    }
  }

  return data;
});

export const dealsListSelector = createSelector([
  filteredDealsAffiliationRequestsSelector,
  (_: any,  config: IDealsTableConfig) => config,
], (data, { sort }): IDealAffiliationRequest[] => {
  if (!data || !data.length) return [];

  const extractor = (item: IDealAffiliationRequest): Date => {
    if (dateFields.has(sort.field)) {
      const noDate = 0 as undefined;
      return item[sort.field] ? new Date(item[sort.field]) : noDate;
    }
    return item[sort.field];
  };

  const sortedData: IDealAffiliationRequest[] = orderBy(data, [ extractor ], [ sort.direction ]);

  return sortedData;
});

export const totalDealsAffiliationRequestsSelector = createSelector(filteredDealsAffiliationRequestsSelector, (data) => data && data.length);

export const hasDealsSelector = createSelector(filteredDealsAffiliationRequestsSelector, (data): boolean => {
  return Boolean(data && data.length);
});

export const madadYearDealsSelector = createSelector(filteredDealsAffiliationRequestsSelector, (data) => {
  if (!(data && data.length)) return [];

  return data.filter(affiliationRequest =>
    Boolean(
      affiliationRequest.dealDate
      && (new Date(affiliationRequest.dealDate)).getFullYear() === YEAR_BEFORE)
      && affiliationRequest.affiliationStatus === DealAffiliationStatus.Approved
  );
});
